import {useAuth as useOidcAuth} from "react-oidc-context";
import {useEffect, useState} from "react";
import {AuthContextType, Role, UserProfile} from "../types/auth.types";
import {updateAuthState} from "../../../services/apiClient";
import cognitoAuthConfig from "../config/authConfig";

/**
 * Hook étendu pour l’authentification via Cognito/OIDC.
 *
 * - Appelle useOidcAuth() sans argument.
 * - Construit un UserProfile avec sub, email, vérification, rôles, etc.
 * - Met à jour le module API client via updateAuthState({ isAuthenticated, accessToken }).
 * - Expose login, logout, signup, userInfo et getFullName.
 */
export const useAuth = (): AuthContextType => {
    const auth = useOidcAuth();

    // Profil enrichi côté front
    const [userInfo, setUserInfo] = useState<UserProfile>({
        sub:            "",
        email:          "",
        email_verified: false,
        roles:          [],
    });

    // Dès que le token est présent, on reconstruit userInfo
    useEffect(() => {
        if (auth.isAuthenticated && auth.user) {
            const profile = auth.user.profile || {};

            // Récupère cognito:groups si c’est un tableau, sinon []
            const rawGroups = Array.isArray(profile["cognito:groups"])
                ? (profile["cognito:groups"] as string[])
                : [];

            // Filtre pour ne garder que nos rôles métiers
            const roles: Role[] = rawGroups
                .filter((g) => Object.values(Role).includes(g as Role))
                .map((g) => g as Role);

            // Construit le UserProfile complet
            const newUser: UserProfile = {
                sub:            profile.sub ?? "",
                email:          profile.email ?? "",
                email_verified: Boolean(profile.email_verified),
                name:           profile.name,
                given_name:     profile.given_name,
                family_name:    profile.family_name,
                picture:        profile.picture,
                roles,
            };

            setUserInfo(newUser);

            // Met à jour l'état d'auth pour Axios
            const token = (auth.user as any).access_token ?? null;
            updateAuthState({ isAuthenticated: true, accessToken: token });
        }
    }, [auth.isAuthenticated, auth.user]);

    // Construit le contexte complet
    const context: AuthContextType = {
        isAuthenticated: auth.isAuthenticated,
        user:            auth.user ?? null,
        isLoading:       auth.isLoading,
        error:           auth.error?.message ?? null,

        login:  () => auth.signinRedirect(),
        signup: () => auth.signinRedirect(),
        logout: async () => {
            try {
                // Ta logique de déconnexion qui fonctionne
                await auth.removeUser();
                await auth.clearStaleState();

                const logoutUrl = new URL(
                    `https://eu-west-1brqpkybv6.auth.eu-west-1.amazoncognito.com/logout`
                );
                logoutUrl.searchParams.append("client_id", cognitoAuthConfig.client_id || "");

                const redirectUri =
                    cognitoAuthConfig.post_logout_redirect_uri ||
                    "https://connext-afdtech.com";
                logoutUrl.searchParams.append("logout_uri", redirectUri);
                logoutUrl.searchParams.append("state", "logout-" + Date.now());

                window.location.href = logoutUrl.toString();
            } catch (error) {
                console.error("Erreur lors de la déconnexion:", error);
                window.location.href = window.location.origin;
            }
            return Promise.resolve();
        },

        // Notre profil enrichi
        userInfo,

        // Retourne le nom complet si dispo, sinon l’email
        getFullName: () => {
            if (userInfo.given_name && userInfo.family_name) {
                return `${userInfo.given_name} ${userInfo.family_name}`
                    .toLowerCase()
                    .trim()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "");
            }
            return userInfo.name ?? userInfo.email
                .toLowerCase()
                .trim()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "");
        },
    };

    return context;
};
